import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListTodo,
  Plus,
  Play,
  Pencil,
  Trash2,
  Clock,
  Store,
  User,
  Calendar,
  X,
  Ban,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Timer,
  CheckCircle2,
  AlertOctagon,
  AlertTriangle,
  Signal,
  ArrowDownCircle,
  Filter,
  RotateCcw,
  SlidersHorizontal,
  Package,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Pause
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, todosAPI, salesOrdersAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { getSalesOrderStatusLabel } from '../constants/salesOrderStatus';
import {
  ModernButton,
  ModernCard,
  ModernInput,
  ModernModal,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import { REFRESH_EVENT } from '../components/Layout/TodoAssignmentAlerts';
import { TZ_CASA, todayYMD, toYMD, getWeekDays } from '../utils/calendarWeek';
import AsyncSelect from 'react-select/async';

const statusLabels = {
  pending: 'En attente',
  in_progress: 'En cours',
  paused: 'En pause',
  completed: 'Terminée',
  cancelled: 'Annulée'
};

const statusStyles = {
  pending: 'bg-amber-100 text-amber-900 border-amber-200',
  in_progress: 'bg-sky-100 text-sky-900 border-sky-200',
  paused: 'bg-violet-100 text-violet-900 border-violet-200',
  completed: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  cancelled: 'bg-neutral-200 text-neutral-700 border-neutral-300'
};

/** Priorité : critique, haute, moyenne, faible — couleurs + icônes */
const priorityLabels = {
  critical: 'Critique',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Faible'
};

const PRIORITY_ORDER = ['critical', 'high', 'medium', 'low'];

const priorityMeta = {
  critical: {
    Icon: AlertOctagon,
    badge:
      'bg-gradient-to-r from-red-600 to-red-700 text-white border-red-800/60 shadow-md shadow-red-500/30',
    formIdle:
      'border-red-300 bg-red-50 text-red-900 hover:bg-red-100 hover:border-red-400',
    formActive:
      'border-red-600 bg-gradient-to-br from-red-600 to-red-700 text-white ring-2 ring-red-400 ring-offset-2 shadow-lg shadow-red-500/35'
  },
  high: {
    Icon: AlertTriangle,
    badge:
      'bg-gradient-to-r from-orange-500 to-orange-600 text-white border-orange-700/50 shadow-md shadow-orange-500/25',
    formIdle:
      'border-orange-300 bg-orange-50 text-orange-950 hover:bg-orange-100 hover:border-orange-400',
    formActive:
      'border-orange-500 bg-gradient-to-br from-orange-500 to-orange-600 text-white ring-2 ring-orange-400 ring-offset-2 shadow-lg shadow-orange-500/30'
  },
  medium: {
    Icon: Signal,
    badge:
      'bg-gradient-to-r from-yellow-300 to-yellow-400 text-yellow-950 border-yellow-500/60 shadow-md shadow-yellow-400/25',
    formIdle:
      'border-yellow-400 bg-yellow-50 text-yellow-950 hover:bg-yellow-100 hover:border-yellow-500',
    formActive:
      'border-yellow-500 bg-gradient-to-br from-yellow-300 to-yellow-400 text-yellow-950 ring-2 ring-yellow-400 ring-offset-2 shadow-lg shadow-yellow-400/30'
  },
  low: {
    Icon: ArrowDownCircle,
    badge:
      'bg-gradient-to-r from-emerald-500 to-green-600 text-white border-emerald-700/40 shadow-md shadow-emerald-500/25',
    formIdle:
      'border-emerald-300 bg-emerald-50 text-emerald-950 hover:bg-emerald-100 hover:border-emerald-400',
    formActive:
      'border-emerald-600 bg-gradient-to-br from-emerald-500 to-green-600 text-white ring-2 ring-emerald-400 ring-offset-2 shadow-lg shadow-emerald-500/25'
  }
};

function PriorityBadge({ priority }) {
  const key = priority && priorityMeta[priority] ? priority : 'medium';
  const { Icon, badge } = priorityMeta[key];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-bold border uppercase tracking-wide ${badge}`}
    >
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 stroke-[2.5]" aria-hidden />
      {priorityLabels[key]}
    </span>
  );
}

function formatDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', {
    timeZone: TZ_CASA,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDueDate(due) {
  const y = toYMD(due);
  if (!y) return '—';
  const [Y, M, D] = y.split('-').map(Number);
  const d = new Date(Y, M - 1, D);
  return d.toLocaleDateString('fr-FR', {
    timeZone: TZ_CASA,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}


/** Bloc commande liée : résumé + détail lignes (chargement à l’ouverture). */
function TodoLinkedOrderSection({ todo }) {
  const oid = todo.sales_order_id;
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!oid) return null;

  const total = Number(todo.order_total_amount);
  const totalLabel = Number.isFinite(total) ? formatCurrency(total) : '—';
  const nItems = Number(todo.order_items_count);
  const itemsLabel = Number.isFinite(nItems) ? nItems : '—';

  const fetchDetail = async () => {
    if (detail != null || loading) return;
    setLoading(true);
    try {
      const res = await salesOrdersAPI.getSalesOrder(oid);
      setDetail(res.data);
    } catch {
      toast.error('Impossible de charger le détail de la commande');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchDetail();
  };

  return (
    <div className="mb-4 rounded-xl border border-teal-200/90 bg-gradient-to-br from-teal-50/90 to-cyan-50/50 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white shadow-md">
          <Package className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-wide text-teal-800">Commande liée</p>
          <p className="text-sm font-bold text-slate-900">
            Commande #{oid}
            {todo.order_client_name ? (
              <span className="font-semibold text-slate-700"> · {todo.order_client_name}</span>
            ) : null}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            {todo.order_client_phone ? (
              <span>Tél. {todo.order_client_phone}</span>
            ) : null}
            {todo.order_status ? (
              <span className="font-semibold text-slate-800">
                Statut : {getSalesOrderStatusLabel(todo.order_status)}
              </span>
            ) : null}
            <span>Total : {totalLabel}</span>
            <span>Lignes : {itemsLabel}</span>
            {todo.order_date_reception ? (
              <span>Réception : {formatDateOnlyFr(todo.order_date_reception)}</span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Link
              to="/ventes/commandes"
              className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Ouvrir les commandes
            </Link>
            <button
              type="button"
              onClick={toggle}
              className="inline-flex items-center gap-1 rounded-lg border border-teal-300 bg-white/80 px-2.5 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-50"
            >
              {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {open ? 'Masquer le détail' : 'Voir articles'}
            </button>
          </div>
          {open && (
            <div className="mt-2 rounded-lg border border-teal-100 bg-white/90 p-3 text-sm">
              {loading && (
                <p className="text-xs text-slate-500 py-2">Chargement des lignes…</p>
              )}
              {!loading && detail?.items?.length > 0 && (
                <ul className="space-y-2">
                  {detail.items.map((it) => (
                    <li
                      key={it.id}
                      className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2 last:border-0 last:pb-0 text-xs"
                    >
                      <span className="font-medium text-slate-800">{it.product_name || `Produit #${it.product_id}`}</span>
                      <span className="text-slate-600 tabular-nums">
                        ×{it.quantity} · {formatCurrency(Number(it.price) * Number(it.quantity))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {!loading && detail && (!detail.items || detail.items.length === 0) && (
                <p className="text-xs text-slate-500">Aucune ligne sur cette commande.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDurationMinutes(m) {
  if (m == null || Number.isNaN(Number(m))) return '—';
  const total = Number(m);
  const min = Math.floor(total);
  const sec = Math.round((total - min) * 60);
  if (min === 0 && sec === 0) return '< 1 min';
  return `${min} min${sec > 0 ? ` ${sec} s` : ''}`;
}

function formatDurationSeconds(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const min = Math.floor(s / 60);
  const sec = s % 60;
  if (min === 0 && sec === 0) return '0 s';
  if (min === 0) return `${sec} s`;
  return `${min} min${sec > 0 ? ` ${sec} s` : ''}`;
}

function LiveElapsed({ startedAt, baseSeconds = 0 }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const start = new Date(startedAt).getTime();
  const elapsed = Math.max(0, Date.now() - start);
  const totalMs = Math.max(0, Number(baseSeconds) || 0) * 1000 + elapsed;
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  return (
    <span className="font-mono tabular-nums text-lg font-bold text-sky-800">
      {minutes} min {String(seconds).padStart(2, '0')} s
    </span>
  );
}

const emptyForm = {
  title: '',
  description: '',
  assigned_to: '',
  due_date: '',
  store_id: '',
  priority: 'medium',
  sales_order_id: ''
};

function orderToSelectOption(o) {
  if (!o || o.id == null) return null;
  return {
    value: String(o.id),
    label: `#${o.id} — ${o.client_name || 'Client'} — ${formatCurrency(Number(o.total) || 0)}`,
    data: o
  };
}

const commandeAsyncStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e1' }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10060 })
};

/** Bouton icône seule (carte tâche — gestion) */
const todoIconBtnBase =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-slate-600 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-40';

const Todos = () => {
  const { user, isAdmin, isManager } = useAuth();
  const canManage = isAdmin() || isManager();

  const canOperateTask = (t) => {
    if (!user?.id) return false;
    if (Number(user.id) === Number(t.assigned_to)) return true;
    return isAdmin() || isManager();
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterStoreId, setFilterStoreId] = useState('all');
  const [quickFilterStatus, setQuickFilterStatus] = useState('all');
  const [quickFilterPriority, setQuickFilterPriority] = useState('all');
  const [quickFilterAssignee, setQuickFilterAssignee] = useState('all');
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDueYMD, setSelectedDueYMD] = useState(() => todayYMD());

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [commandeAsyncValue, setCommandeAsyncValue] = useState(null);
  const orderSearchTimerRef = useRef(null);

  const loadEmployees = useCallback(async (storeId) => {
    try {
      const res = await authAPI.getEmployees();
      let list = res.data || [];
      if (storeId && storeId !== 'all') {
        list = list.filter((e) => e.store_id === Number(storeId));
      }
      setEmployees(list);
    } catch {
      setEmployees([]);
    }
  }, []);

  const loadStores = useCallback(async () => {
    if (!isAdmin()) return;
    try {
      const res = await authAPI.getStores();
      setStores(res.data || []);
    } catch {
      setStores([]);
    }
  }, [isAdmin]);

  const loadTodos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin() && filterStoreId && filterStoreId !== 'all') {
        params.store_id = filterStoreId;
      }
      const res = await todosAPI.list(params);
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les tâches');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filterStoreId]);

  /** Met à jour une tâche dans la liste sans recharger (évite le spinner plein écran). */
  const mergeTodoFromServer = useCallback((updated) => {
    if (!updated || updated.id == null) return;
    setItems((prev) => {
      const i = prev.findIndex((t) => Number(t.id) === Number(updated.id));
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...next[i], ...updated };
      return next;
    });
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    if (!canManage) return;
    if (isAdmin()) {
      if (filterStoreId !== 'all') {
        loadEmployees(filterStoreId);
      } else {
        loadEmployees('all');
      }
    } else {
      loadEmployees(user?.store_id);
    }
  }, [canManage, isAdmin, filterStoreId, user?.store_id, loadEmployees]);

  useEffect(() => {
    if (isAdmin()) setQuickFilterAssignee('all');
  }, [filterStoreId, isAdmin]);

  const loadOrderOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (orderSearchTimerRef.current) clearTimeout(orderSearchTimerRef.current);
        orderSearchTimerRef.current = setTimeout(async () => {
          try {
            const sid = isAdmin() ? form.store_id : String(user?.store_id || '');
            if (isAdmin() && !sid) {
              resolve([]);
              return;
            }
            const params = { limit: 100 };
            const q = (inputValue || '').trim();
            if (q) params.search = q;
            if (isAdmin() && sid) params.store_id = sid;
            const res = await salesOrdersAPI.getSalesOrders(params);
            const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            resolve(rows.map(orderToSelectOption).filter(Boolean));
          } catch {
            resolve([]);
          }
        }, 280);
      }),
    [isAdmin, form.store_id, user?.store_id]
  );

  const openCreate = () => {
    setEditingId(null);
    setCommandeAsyncValue(null);
    setForm({
      ...emptyForm,
      priority: 'medium',
      due_date: todayYMD(),
      store_id: isAdmin() ? (filterStoreId !== 'all' ? String(filterStoreId) : '') : String(user?.store_id || ''),
      sales_order_id: ''
    });
    setModalOpen(true);
    if (isAdmin() && filterStoreId !== 'all') {
      loadEmployees(filterStoreId);
    } else if (!isAdmin()) {
      loadEmployees(user?.store_id);
    }
  };

  const openEdit = (row) => {
    if (!canManage || row.status === 'completed') return;
    setEditingId(row.id);
    setCommandeAsyncValue(null);
    setForm({
      title: row.title || '',
      description: row.description || '',
      assigned_to: String(row.assigned_to),
      due_date: toYMD(row.due_date) || todayYMD(),
      store_id: String(row.store_id),
      priority: row.priority && priorityLabels[row.priority] ? row.priority : 'medium',
      sales_order_id: row.sales_order_id != null && row.sales_order_id !== '' ? String(row.sales_order_id) : ''
    });
    setModalOpen(true);
    loadEmployees(row.store_id);
    if (row.sales_order_id) {
      salesOrdersAPI
        .getSalesOrder(row.sales_order_id)
        .then((res) => {
          const opt = orderToSelectOption(res.data);
          if (opt) setCommandeAsyncValue(opt);
        })
        .catch(() => {
          setCommandeAsyncValue({
            value: String(row.sales_order_id),
            label: `Commande #${row.sales_order_id}`,
            data: null
          });
        });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!form.assigned_to) {
      toast.error('Choisissez un employé');
      return;
    }
    const storeId = isAdmin() ? form.store_id : user?.store_id;
    if (!storeId) {
      toast.error('Magasin requis');
      return;
    }
    if (!form.due_date || !/^\d{4}-\d{2}-\d{2}$/.test(form.due_date)) {
      toast.error("La date d'échéance est requise");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        assigned_to: Number(form.assigned_to),
        due_date: form.due_date,
        priority: form.priority || 'medium'
      };
      if (isAdmin()) {
        payload.store_id = Number(storeId);
      }
      payload.sales_order_id = form.sales_order_id ? Number(form.sales_order_id) : null;
      if (editingId) {
        await todosAPI.update(editingId, payload);
        toast.success('Tâche mise à jour');
      } else {
        await todosAPI.create(payload);
        toast.success('Tâche créée');
      }
      setModalOpen(false);
      loadTodos();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg;
      toast.error(msg || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try {
      await todosAPI.remove(id);
      setItems((prev) => prev.filter((t) => Number(t.id) !== Number(id)));
      toast.success('Tâche supprimée');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const handleStart = async (id) => {
    try {
      const res = await todosAPI.start(id);
      mergeTodoFromServer(res.data);
      toast.success('Chronomètre démarré');
      window.dispatchEvent(new Event(REFRESH_EVENT));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const handleFinish = async (id) => {
    try {
      const res = await todosAPI.finish(id);
      mergeTodoFromServer(res.data);
      toast.success('Tâche terminée');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette tâche ?')) return;
    try {
      const res = await todosAPI.cancel(id);
      mergeTodoFromServer(res.data);
      toast.success('Tâche annulée');
      window.dispatchEvent(new Event(REFRESH_EVENT));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const handlePause = async (id) => {
    try {
      const res = await todosAPI.pause(id);
      mergeTodoFromServer(res.data);
      toast.success('Tâche mise en pause');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const handleResume = async (id) => {
    try {
      const res = await todosAPI.resume(id);
      mergeTodoFromServer(res.data);
      toast.success('Tâche reprise');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const stats = useMemo(() => {
    const pending = items.filter((t) => t.status === 'pending').length;
    const active = items.filter((t) => t.status === 'in_progress').length;
    const paused = items.filter((t) => t.status === 'paused').length;
    const done = items.filter((t) => t.status === 'completed').length;
    return { pending, active, paused, done, total: items.length };
  }, [items]);

  const weekDays = useMemo(() => getWeekDays(weekOffset), [weekOffset]);

  const filteredItems = useMemo(() => {
    if (selectedDueYMD == null) return items;
    return items.filter((t) => toYMD(t.due_date) === selectedDueYMD);
  }, [items, selectedDueYMD]);

  const displayItems = useMemo(() => {
    let list = filteredItems;
    if (quickFilterStatus !== 'all') {
      list = list.filter((t) => t.status === quickFilterStatus);
    }
    if (quickFilterPriority !== 'all') {
      list = list.filter((t) => (t.priority || 'medium') === quickFilterPriority);
    }
    if (quickFilterAssignee !== 'all') {
      list = list.filter((t) => String(t.assigned_to) === String(quickFilterAssignee));
    }
    return list;
  }, [filteredItems, quickFilterStatus, quickFilterPriority, quickFilterAssignee]);

  const itemsWithoutDue = useMemo(() => items.filter((t) => !toYMD(t.due_date)), [items]);

  const resetQuickFilters = useCallback(() => {
    setQuickFilterStatus('all');
    setQuickFilterPriority('all');
    setQuickFilterAssignee('all');
    setSelectedDueYMD(null);
  }, []);

  return (
    <div className="w-full min-h-[calc(100dvh-4rem)] bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/60 py-6 sm:py-8">
      <div className="w-full max-w-none">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 mb-3"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Tableau de bord
            </Link>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
              <span className="p-2 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg">
                <ListTodo className="w-7 h-7 sm:w-8 sm:h-8" />
              </span>
              Tâches & chronos
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl text-sm sm:text-base">
              {canManage
                ? "Créez des tâches avec une date d'échéance ; le créneau réel (début / fin) est enregistré automatiquement quand l'employé démarre et termine."
                : "Vos tâches : démarrez et terminez pour enregistrer le créneau réel et la durée."}
            </p>
          </div>
          {canManage && (
            <ModernButton
              type="button"
              variant="primary"
              className="shrink-0 shadow-lg shadow-indigo-500/25"
              onClick={openCreate}
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle tâche
            </ModernButton>
          )}
        </div>

        {canManage && (
          <ModernCard className="mb-6 p-4 sm:p-5 border border-indigo-100/80 bg-white/80 backdrop-blur-sm shadow-md rounded-2xl">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-indigo-100 text-indigo-700">
                  <SlidersHorizontal className="w-4 h-4" />
                </span>
                Filtres rapides
              </h2>
              <ModernButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={resetQuickFilters}
                className="shrink-0"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Tout afficher
              </ModernButton>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {isAdmin() && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                    <Store className="w-3.5 h-3.5 text-indigo-500" />
                    Magasin
                  </label>
                  <select
                    value={filterStoreId}
                    onChange={(e) => setFilterStoreId(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">Tous les magasins</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.city ? `— ${s.city}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5 text-sky-600" />
                  Statut
                </label>
                <select
                  value={quickFilterStatus}
                  onChange={(e) => setQuickFilterStatus(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="in_progress">En cours</option>
                  <option value="paused">En pause</option>
                  <option value="completed">Terminée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  Priorité
                </label>
                <select
                  value={quickFilterPriority}
                  onChange={(e) => setQuickFilterPriority(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Toutes les priorités</option>
                  {PRIORITY_ORDER.map((p) => (
                    <option key={p} value={p}>
                      {priorityLabels[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-violet-600" />
                  Assigné à
                </label>
                <select
                  value={quickFilterAssignee}
                  onChange={(e) => setQuickFilterAssignee(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Tous les employés</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name || e.email || `Employé #${e.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              « Tout afficher » réinitialise les filtres et le jour sélectionné dans le calendrier.
            </p>
          </ModernCard>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, icon: ListTodo, color: 'from-slate-600 to-slate-800' },
            { label: 'En attente', value: stats.pending, icon: Clock, color: 'from-amber-500 to-orange-600' },
            { label: 'En cours', value: stats.active, icon: Timer, color: 'from-sky-500 to-blue-600' },
            { label: 'En pause', value: stats.paused, icon: Pause, color: 'from-violet-500 to-purple-600' },
            { label: 'Terminées', value: stats.done, icon: CheckCircle2, color: 'from-emerald-500 to-teal-600' }
          ].map((s) => (
            <div
              key={s.label}
              className={`rounded-2xl p-4 sm:p-5 text-white shadow-lg bg-gradient-to-br ${s.color} flex items-center justify-between gap-2`}
            >
              <div>
                <p className="text-xs sm:text-sm font-medium text-white/90">{s.label}</p>
                <p className="text-2xl sm:text-3xl font-black tabular-nums">{s.value}</p>
              </div>
              <s.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white/40 shrink-0" />
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <ModernLoadingSpinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <ModernCard className="p-12 text-center rounded-3xl border border-dashed border-slate-200 bg-white/60">
            <ListTodo className="w-14 h-14 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 font-medium">Aucune tâche pour le moment.</p>
            {canManage && (
              <ModernButton type="button" variant="primary" className="mt-6" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une tâche
              </ModernButton>
            )}
          </ModernCard>
        ) : (
          <>
            <ModernCard className="mb-6 p-4 sm:p-5 rounded-2xl border border-indigo-100 bg-white/95 shadow-md overflow-x-auto">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setWeekOffset((w) => w - 1)}
                    className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                    aria-label="Semaine précédente"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeekOffset((w) => w + 1)}
                    className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                    aria-label="Semaine suivante"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm sm:text-base font-semibold text-slate-800 text-center flex-1 min-w-[12rem]">
                  {weekDays[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} —{' '}
                  {weekDays[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <div className="flex items-center gap-2">
                  {selectedDueYMD != null && (
                    <ModernButton type="button" variant="secondary" size="sm" onClick={() => setSelectedDueYMD(null)}>
                      Tout afficher
                    </ModernButton>
                  )}
                  <ModernButton type="button" variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>
                    Aujourd&apos;hui
                  </ModernButton>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Cliquez sur un jour pour filtrer les tâches dont la date d&apos;échéance tombe ce jour-là.
              </p>
              <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[640px] sm:min-w-0">
                {weekDays.map((day) => {
                  const ymd = toYMD(day);
                  const dayTodos = items.filter((t) => toYMD(t.due_date) === ymd);
                  const isToday = todayYMD() === ymd;
                  const isSelected = selectedDueYMD === ymd;
                  return (
                    <button
                      key={ymd}
                      type="button"
                      onClick={() => setSelectedDueYMD((prev) => (prev === ymd ? null : ymd))}
                      className={`rounded-xl border-2 p-2 sm:p-3 text-left transition-all duration-200 flex flex-col min-h-[120px] sm:min-h-[140px] ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 shadow-md'
                          : isToday
                            ? 'border-violet-300 bg-violet-50/80 hover:border-violet-400'
                            : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white'
                      }`}
                    >
                      <span className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase">
                        {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                      </span>
                      <span
                        className={`text-lg sm:text-2xl font-black tabular-nums leading-tight ${
                          isToday ? 'text-violet-700' : 'text-slate-900'
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      <span className="text-[10px] text-slate-400 mb-1">{dayTodos.length} tâche{dayTodos.length !== 1 ? 's' : ''}</span>
                      <div className="flex-1 space-y-0.5 overflow-hidden">
                        {dayTodos.slice(0, 3).map((t) => (
                          <div
                            key={t.id}
                            className="text-[9px] sm:text-[10px] leading-tight text-slate-700 truncate rounded px-0.5 bg-white/60 border border-slate-100"
                            title={t.title}
                          >
                            {t.title}
                          </div>
                        ))}
                        {dayTodos.length > 3 && (
                          <span className="text-[9px] font-semibold text-indigo-600">+{dayTodos.length - 3}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {itemsWithoutDue.length > 0 && (
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  {itemsWithoutDue.length} tâche{itemsWithoutDue.length !== 1 ? 's' : ''} sans date d&apos;échéance (non affichées dans le calendrier).
                </p>
              )}
            </ModernCard>

            {filteredItems.length === 0 ? (
              <ModernCard className="p-10 text-center rounded-2xl border border-dashed border-slate-200 bg-white/70">
                <p className="text-slate-700 font-medium mb-4">Aucune tâche pour le jour sélectionné.</p>
                <ModernButton type="button" variant="secondary" onClick={() => setSelectedDueYMD(null)}>
                  Afficher toutes les tâches
                </ModernButton>
              </ModernCard>
            ) : displayItems.length === 0 ? (
              <ModernCard className="p-10 text-center rounded-2xl border border-dashed border-amber-200 bg-amber-50/40">
                <p className="text-slate-700 font-medium mb-2">Aucune tâche ne correspond aux filtres.</p>
                <p className="text-sm text-slate-500 mb-4">Modifiez les filtres ou réinitialisez pour tout voir.</p>
                <ModernButton type="button" variant="secondary" onClick={resetQuickFilters}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Tout afficher
                </ModernButton>
              </ModernCard>
            ) : (
          <ul className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {displayItems.map((t) => (
              <li key={t.id}>
                <ModernCard className="h-full p-5 sm:p-6 rounded-2xl border border-slate-100 bg-white/90 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <PriorityBadge priority={t.priority || 'medium'} />
                      </div>
                      <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-snug break-words">
                        {t.title}
                      </h2>
                      {isAdmin() && (
                        <p className="text-xs sm:text-sm text-slate-500 mt-1 flex items-center gap-1">
                          <Store className="w-3.5 h-3.5 shrink-0" />
                          {t.store_name}
                        </p>
                      )}
                    </div>
                    <span
                      className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold border ${statusStyles[t.status] || statusStyles.pending}`}
                    >
                      {statusLabels[t.status] || t.status}
                    </span>
                  </div>

                  {t.description && (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-4">{t.description}</p>
                  )}

                  <TodoLinkedOrderSection todo={t} />

                  {t.status === 'paused' && !canManage && (
                    <p className="text-xs text-violet-900 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 mb-3">
                      Tâche mise en pause par un responsable. Demandez la reprise pour continuer.
                    </p>
                  )}

                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-slate-700">Échéance : </span>
                        {formatDueDate(t.due_date)}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Timer className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                      <div className="text-xs sm:text-sm leading-relaxed">
                        <span className="font-semibold text-slate-700">Créneau réel : </span>
                        {t.status === 'paused' ? (
                          <span className="text-slate-600">
                            {t.paused_from === 'pending'
                              ? 'Pas encore démarrée — reprise par un responsable pour poursuivre.'
                              : Number(t.accumulated_active_seconds) > 0
                                ? `Temps déjà enregistré avant pause : ${formatDurationSeconds(t.accumulated_active_seconds)}.`
                                : 'Session interrompue en pause — reprise pour continuer le chrono.'}
                          </span>
                        ) : (
                          <>
                            {!t.actual_start_at && (
                              <span className="text-slate-500">
                                Début et fin enregistrés automatiquement aux clics Démarrer et Terminer.
                              </span>
                            )}
                            {t.actual_start_at && !t.actual_finish_at && (
                              <>
                                début {formatDateTime(t.actual_start_at)} · fin{' '}
                                <span className="text-sky-700 font-semibold">en cours</span>
                              </>
                            )}
                            {t.actual_start_at && t.actual_finish_at && (
                              <>
                                {formatDateTime(t.actual_start_at)} → {formatDateTime(t.actual_finish_at)}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-indigo-500 shrink-0" />
                      <span className="truncate">{t.assignee_name}</span>
                    </div>
                  </div>

                  {t.status === 'in_progress' && t.actual_start_at && (
                    <div className="mb-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                      <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide mb-1 flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        Temps écoulé
                      </p>
                      <LiveElapsed
                        startedAt={t.actual_start_at}
                        baseSeconds={Number(t.accumulated_active_seconds) || 0}
                      />
                      {Number(t.accumulated_active_seconds) > 0 && (
                        <p className="text-[11px] text-sky-700 mt-1.5">
                          Dont {formatDurationSeconds(t.accumulated_active_seconds)} avant la dernière reprise.
                        </p>
                      )}
                    </div>
                  )}

                  {t.status === 'completed' && (
                    <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                      <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1">
                        Durée totale enregistrée
                      </p>
                      <p className="text-2xl font-bold text-emerald-900 font-mono tabular-nums">
                        {formatDurationMinutes(t.duration_minutes)}
                      </p>
                      <p className="text-xs text-emerald-700 mt-1">
                        Début : {formatDateTime(t.actual_start_at)} · Fin :{' '}
                        {formatDateTime(t.actual_finish_at)}
                      </p>
                    </div>
                  )}

                  <div className="mt-auto pt-4 border-t border-slate-100">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        {(t.status === 'pending' || t.status === 'in_progress') && canOperateTask(t) && (
                          <>
                            {t.status === 'pending' && (
                              <button
                                type="button"
                                onClick={() => handleStart(t.id)}
                                className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold text-white shadow-md shadow-cyan-500/20 transition hover:brightness-105 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600"
                              >
                                <Play className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                                Démarrer
                              </button>
                            )}
                            {t.status === 'in_progress' && (
                              <button
                                type="button"
                                onClick={() => handleFinish(t.id)}
                                className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold text-white shadow-md shadow-emerald-500/25 transition hover:brightness-105 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600"
                              >
                                <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                                Terminer
                              </button>
                            )}
                          </>
                        )}
                        {canManage && (t.status === 'pending' || t.status === 'in_progress') && (
                          <button
                            type="button"
                            onClick={() => handlePause(t.id)}
                            className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2 text-sm font-semibold text-violet-900 shadow-sm transition hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2"
                          >
                            <Pause className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                            Pause
                          </button>
                        )}
                      </div>

                      {canManage && t.status !== 'completed' && (
                        <div
                          className="flex items-center gap-1 shrink-0 rounded-lg border border-slate-200/80 bg-slate-50/90 p-1"
                          role="toolbar"
                          aria-label="Gestion de la tâche"
                        >
                          {t.status === 'paused' && (
                            <button
                              type="button"
                              title="Reprendre"
                              aria-label="Reprendre la tâche"
                              onClick={() => handleResume(t.id)}
                              className={`${todoIconBtnBase} border-cyan-200/90 bg-cyan-50/90 text-cyan-800 hover:bg-cyan-100`}
                            >
                              <Play className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                            </button>
                          )}
                          <button
                            type="button"
                            title="Modifier"
                            aria-label="Modifier la tâche"
                            onClick={() => openEdit(t)}
                            disabled={t.status === 'cancelled'}
                            className={`${todoIconBtnBase} border-slate-200 bg-white hover:bg-slate-50 hover:text-slate-900`}
                          >
                            <Pencil className="h-4 w-4" strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            title="Annuler la tâche"
                            aria-label="Annuler la tâche"
                            onClick={() => handleCancel(t.id)}
                            disabled={t.status === 'cancelled'}
                            className={`${todoIconBtnBase} border-amber-200/90 bg-amber-50/90 text-amber-900 hover:bg-amber-100`}
                          >
                            <Ban className="h-4 w-4" strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            title="Supprimer"
                            aria-label="Supprimer la tâche"
                            onClick={() => handleDelete(t.id)}
                            className={`${todoIconBtnBase} border-red-200/90 bg-red-50/80 text-red-700 hover:bg-red-100`}
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </ModernCard>
              </li>
            ))}
          </ul>
            )}
          </>
        )}
      </div>

      <ModernModal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editingId ? 'Modifier la tâche' : 'Nouvelle tâche'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdmin() && !editingId && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Magasin *</label>
              <select
                required
                value={form.store_id}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, store_id: v, assigned_to: '', sales_order_id: '' }));
                  setCommandeAsyncValue(null);
                  loadEmployees(v);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">— Choisir —</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <ModernInput
            label="Titre *"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Priorité *</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PRIORITY_ORDER.map((key) => {
                const { Icon, formIdle, formActive } = priorityMeta[key];
                const selected = form.priority === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, priority: key }))}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-2 py-3 text-center transition-all duration-200 ${
                      selected ? formActive : formIdle
                    }`}
                  >
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 shrink-0 text-current" strokeWidth={2.25} />
                    <span className="text-[10px] sm:text-xs font-bold leading-tight">{priorityLabels[key]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date d&apos;échéance *</label>
            <input
              type="date"
              required
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assigner à *</label>
            <select
              required
              value={form.assigned_to}
              onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Employé —</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} ({e.role})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Commande (optionnel)</label>
            <AsyncSelect
              key={isAdmin() ? `cmd-${form.store_id || 'x'}` : 'cmd-mgr'}
              instanceId="todo-commande-async"
              cacheOptions
              defaultOptions
              loadOptions={loadOrderOptions}
              value={commandeAsyncValue}
              onChange={(opt) => {
                setForm((f) => ({ ...f, sales_order_id: opt?.value ?? '' }));
                setCommandeAsyncValue(opt || null);
              }}
              placeholder="Rechercher n° commande, nom client, téléphone…"
              isClearable
              isDisabled={isAdmin() && !form.store_id && !editingId}
              noOptionsMessage={({ inputValue }) => {
                if (isAdmin() && !form.store_id) return 'Choisissez d’abord un magasin';
                if ((inputValue || '').trim()) return 'Aucune commande trouvée';
                return 'Tapez pour rechercher ou ouvrez la liste';
              }}
              loadingMessage={() => 'Chargement…'}
              styles={commandeAsyncStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              classNamePrefix="todo-commande"
            />
            <p className="text-xs text-slate-500 mt-1">
              Liste filtrée côté serveur (client / téléphone / n°). Les 100 premiers résultats s’affichent.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <ModernButton type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1" />
              Fermer
            </ModernButton>
            <ModernButton type="submit" variant="primary" disabled={saving}>
              {saving ? '…' : editingId ? 'Enregistrer' : 'Créer'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Todos;
